import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";
import { api, apiUpload, fetchImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { UserCircle, BadgeCheck, Clock, PenLine, ShieldCheck, Feather, IdCard, Upload, XCircle, RefreshCw } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

function VerificationCard({ user }) {
  const [ver, setVer] = useState(undefined);
  const [mineImage, setMineImage] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const fileInput = useRef(null);

  const load = () => api("/verification/mine").then(setVer).catch(() => setVer(null));

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    let url;
    if (ver && ver.status && ver.status !== "none") {
      fetchImageUrl("/verification/mine/image")
        .then((u) => { url = u; setMineImage(u); })
        .catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [ver]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("يُسمح بالصور فقط (JPG أو PNG)");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("حجم الصورة يتجاوز 8 ميغابايت");
      return;
    }
    setFile(f);
    setPreview((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(f); });
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await apiUpload("/verification/upload", fd);
      toast.success("تم رفع هويتك الجامعية — بانتظار مراجعة الإدارة");
      setFile(null);
      setPreview((old) => { if (old) URL.revokeObjectURL(old); return null; });
      setReplacing(false);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  const showForm = !ver || ver.status === "none" || ver.status === "rejected" || replacing;

  return (
    <motion.div
      className="mt-6 rounded-2xl border border-gold/25 bg-white p-6 shadow-sm sm:p-8"
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2, ease: EASE }}
      data-testid="verification-card"
    >
      <h2 className="mb-2 flex items-center gap-2 font-amiri text-2xl text-navy">
        <IdCard className="h-6 w-6 text-gold-dark" />
        التحقق الطلابي
      </h2>
      <p className="mb-6 text-sm leading-7 text-muted-foreground">
        ارفع صورة هويتك الجامعية ليتأكد للإدارة أنك طالب، فتُقبل عضويتك بشكل رسمي.
      </p>

      {ver === undefined ? (
        <p className="py-6 text-center text-sm text-muted-foreground">جارٍ التحميل...</p>
      ) : (
        <>
          {ver.status === "approved" && (
            <div className="flex items-center gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-50 p-5" data-testid="verification-approved">
              <BadgeCheck className="h-6 w-6 shrink-0 text-emerald-600" />
              <p className="text-sm font-bold text-emerald-700">هويتك الجامعية مقبولة — عضويتك موثقة رسمياً.</p>
            </div>
          )}

          {ver.status === "pending" && !replacing && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-amber-500/40 bg-amber-50 p-5" data-testid="verification-pending">
                <Clock className="h-6 w-6 shrink-0 text-amber-500" />
                <p className="flex-1 text-sm font-bold text-amber-700">
                  هويتك قيد المراجعة من الإدارة
                  {ver.created_at && (
                    <span className="block text-xs font-normal">
                      رُفعت في {format(new Date(ver.created_at), "d MMMM yyyy", { locale: ar })}
                    </span>
                  )}
                </p>
                <Button
                  variant="outline"
                  onClick={() => setReplacing(true)}
                  data-testid="verification-replace-button"
                  className="rounded-full border-amber-500/50 font-bold text-amber-700 hover:bg-amber-100"
                >
                  <RefreshCw className="ml-1.5 h-4 w-4" /> استبدال الصورة
                </Button>
              </div>
              {mineImage && (
                <img src={mineImage} alt="الهوية المرفوعة" className="max-h-64 rounded-2xl border border-gold/25 object-contain" data-testid="verification-preview" />
              )}
            </div>
          )}

          {ver.status === "rejected" && (
            <div className="mb-4 flex items-center gap-3 rounded-2xl border border-red-400/40 bg-red-50 p-5" data-testid="verification-rejected">
              <XCircle className="h-6 w-6 shrink-0 text-red-500" />
              <p className="text-sm font-bold text-red-600">رُفضت الصورة السابقة — تأكد من وضوح الهوية وأعد الرفع.</p>
            </div>
          )}

          {showForm && (
            <div className="space-y-4" data-testid="verification-upload-form">
              <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="verification-file-input" />
              <button
                onClick={() => fileInput.current?.click()}
                data-testid="verification-pick-button"
                className="flex w-full flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-gold/40 bg-[#FAF8F3] p-8 transition-[border-color,background-color] duration-300 hover:border-gold hover:bg-gold/5"
              >
                <Upload className="h-8 w-8 text-gold-dark" />
                <span className="text-sm font-bold text-navy">
                  {file ? file.name : "اضغط لاختيار صورة الهوية الجامعية"}
                </span>
                <span className="text-xs text-muted-foreground">JPG أو PNG — بحد أقصى 8 ميغابايت</span>
              </button>
              {preview && (
                <img src={preview} alt="معاينة الهوية" className="max-h-64 rounded-2xl border border-gold/25 object-contain" data-testid="verification-upload-preview" />
              )}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={upload}
                  disabled={!file || uploading}
                  data-testid="verification-upload-button"
                  className="rounded-full bg-gold px-8 font-bold text-navy transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-50"
                >
                  <Upload className="ml-2 h-4 w-4" />
                  {uploading ? "جارٍ الرفع..." : "رفع الهوية"}
                </Button>
                {replacing && (
                  <Button variant="outline" onClick={() => setReplacing(false)} className="rounded-full border-gold/40 font-bold text-navy hover:bg-gold/10" data-testid="verification-cancel-replace">
                    إلغاء
                  </Button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
}

export default function Account() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user.name);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim() || name.trim() === user.name) return;
    setSaving(true);
    try {
      const updated = await api("/users/me", { method: "PATCH", body: JSON.stringify({ name: name.trim() }) });
      setUser(updated);
      toast.success("تم تحديث اسمك بنجاح");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="account-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
        <h1 className="mb-2 flex items-center gap-3 font-amiri text-4xl text-navy sm:text-5xl">
          <UserCircle className="h-8 w-8 text-gold-dark" />
          حسابي
        </h1>
        <p className="mb-8 text-muted-foreground">بيانات عضويتك في اتحاد نابو.</p>
      </motion.div>

      <motion.div
        className="rounded-2xl border border-gold/25 bg-white p-6 shadow-sm sm:p-8"
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
        data-testid="account-card"
      >
        <div className="mb-8 flex flex-wrap items-center gap-5">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="h-20 w-20 rounded-2xl border border-gold/40" referrerPolicy="no-referrer" data-testid="account-avatar" />
          ) : (
            <span className="flex h-20 w-20 items-center justify-center rounded-2xl bg-navy font-amiri text-4xl text-gold" data-testid="account-avatar">
              {user.name?.[0] || "ن"}
            </span>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {user.role === "admin" ? (
              <span className="flex items-center gap-1.5 rounded-full border border-gold/50 bg-gold/10 px-3 py-1 text-xs font-bold text-gold-dark" data-testid="account-role-badge">
                <ShieldCheck className="h-3.5 w-3.5" /> أدمن
              </span>
            ) : (
              <span className="rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600" data-testid="account-role-badge">
                عضو
              </span>
            )}
            {user.is_active ? (
              <span className="flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-600" data-testid="account-status-badge">
                <BadgeCheck className="h-3.5 w-3.5" /> حساب مفعَّل
              </span>
            ) : (
              <span className="flex items-center gap-1.5 rounded-full border border-amber-500/50 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-600" data-testid="account-status-badge">
                <Clock className="h-3.5 w-3.5" /> بانتظار التفعيل
              </span>
            )}
            {user.can_post && (
              <span className="flex items-center gap-1.5 rounded-full border border-gold/50 bg-gold/10 px-3 py-1 text-xs font-bold text-gold-dark" data-testid="account-canpost-badge">
                <Feather className="h-3.5 w-3.5" /> إذن النشر
              </span>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-muted-foreground" htmlFor="account-name-input">
              <PenLine className="h-4 w-4 text-gold-dark" />
              الاسم المعروض في الاتحاد
            </label>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Input
                id="account-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="account-name-input"
                maxLength={60}
                className="border-input bg-slate-50 text-navy focus-visible:ring-gold"
              />
              <Button
                onClick={save}
                disabled={saving || !name.trim() || name.trim() === user.name}
                data-testid="account-save-button"
                className="rounded-full bg-gold px-8 font-bold text-navy transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-50"
              >
                {saving ? "جارٍ الحفظ..." : "حفظ الاسم"}
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">هذا الاسم يظهر في منشوراتك وتصويتاتك بدلاً من اسم حساب Google.</p>
          </div>

          <div className="grid grid-cols-1 gap-4 border-t border-gold/20 pt-6 sm:grid-cols-2">
            <div>
              <p className="text-xs text-muted-foreground">البريد الإلكتروني</p>
              <p className="mt-1 font-medium text-navy" dir="ltr" data-testid="account-email">{user.email}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">تاريخ الانضمام</p>
              <p className="mt-1 font-medium text-navy" data-testid="account-joined">
                {user.created_at ? format(new Date(user.created_at), "d MMMM yyyy", { locale: ar }) : "—"}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <VerificationCard user={user} />
    </div>
  );
}
